/**
 * Client-side download filename assembly. The zip name is built here (the UI
 * saves the blob itself, ignoring the backend's Content-Disposition); the
 * image name is normally the backend's Content-Disposition filename — the
 * builder here is the fallback for old backends during rollout.
 */

const SIZE_LABELS: Record<string, string> = {
  fullSize: 'Large',
  '1920w': 'Medium',
  '830w': 'Small'
};

// Characters illegal in filenames on at least one major OS. Each is replaced
// with a space (not removed) so word boundaries survive ("1/2" → "1 2"),
// matching the backend's DownloadNaming rule; runs then collapse to one space.
const stripIllegalFilenameChars = (value: string): string =>
  value
    .replace(/[/\\:*?"<>|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export interface ZipNameParts {
  zipNameBase: string;
  sizeKey: string;
  is43: boolean;
  watermarked: boolean;
}

export const buildZipFileName = ({
  zipNameBase,
  sizeKey,
  is43,
  watermarked
}: ZipNameParts): string => {
  const segments = [
    stripIllegalFilenameChars(zipNameBase) || 'planomatic',
    SIZE_LABELS[sizeKey] ?? sizeKey
  ];
  if (is43) segments.push('4_3');
  if (watermarked) segments.push('Watermarked');
  return `${segments.join(' - ')}.zip`;
};

export const buildImageFileName = (
  sequence: number,
  caption: string | null
): string => {
  const cleanCaption = stripIllegalFilenameChars(caption ?? '') || 'photo';
  return `${String(sequence).padStart(3, '0')} - ${cleanCaption}.jpg`;
};

/**
 * "Address 1[ - Address 2]" from raw property fields, falling back to the
 * pre-comma segment of the formatted display address ("addr1[ unit], city,
 * state zip" — see usePropertyAssets.fetchPropertyMeta) for the brief window
 * where only router-state data exists.
 */
export const buildZipNameBase = (
  address1: string | undefined,
  unitNumber: string | null | undefined,
  formattedAddressFallback: string
): string => {
  if (address1) {
    return unitNumber ? `${address1} - ${unitNumber}` : address1;
  }
  return formattedAddressFallback.split(',')[0].trim();
};
